import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faPlus, faTrash, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAlerts } from '../contexts/AlertsContext';
import { useQuote } from '../contexts/QuoteContext';

const TYPE_LABELS = {
  price_above: 'Price ≥',
  price_below: 'Price ≤',
  pct_up: 'Day % ≥',
  pct_down: 'Day % ≤',
};

const AlertsPanel = ({ open: openProp, onClose, hideTrigger = false }) => {
  const { alerts, lastEvent, clearLastEvent, addAlert, removeAlert, clearTriggered, requestPermission } = useAlerts();
  const { selectSymbol } = useQuote();
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const setOpen = (next) => {
    if (!controlled) setInternalOpen(next);
    if (!next) onClose?.();
  };
  const [symbol, setSymbol] = useState('');
  const [type, setType] = useState('price_above');
  const [threshold, setThreshold] = useState('');
  const [message, setMessage] = useState('');

  const activeCount = useMemo(() => alerts.filter((a) => a.active && !a.triggeredAt).length, [alerts]);
  const triggeredCount = useMemo(() => alerts.filter((a) => a.triggeredAt).length, [alerts]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const created = await addAlert({
      symbol,
      type,
      threshold: parseFloat(threshold),
    });
    if (!created) {
      setMessage('Enter a valid symbol and threshold');
      return;
    }
    setMessage(`Alert set for ${created.symbol}`);
    setSymbol('');
    setThreshold('');
    setTimeout(() => setMessage(''), 2500);
  };

  return (
    <>
      {lastEvent &&
        createPortal(
          <div className="fixed top-14 left-1/2 -translate-x-1/2 z-[210] w-[min(440px,92vw)]">
            <div className="lt-card px-3 py-2.5 flex items-start gap-2 border-[rgba(232,184,74,0.35)] shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
              <FontAwesomeIcon icon={faBell} className="text-[var(--lt-warning)] mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-ink">Alert triggered</div>
                <button
                  type="button"
                  className="text-xs text-muted hover:text-ink text-left"
                  onClick={() => {
                    selectSymbol(lastEvent.symbol);
                    clearLastEvent();
                  }}
                >
                  {lastEvent.detail}
                </button>
              </div>
              <button
                type="button"
                onClick={clearLastEvent}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
          </div>,
          document.body
        )}

      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setInternalOpen(true)}
          className="h-11 px-3 rounded-md border border-hairline bg-surface text-ink hover:bg-elevated text-xs font-semibold inline-flex items-center gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.28)]"
          title="Price alerts"
        >
          <FontAwesomeIcon icon={faBell} />
          Alerts
          {activeCount > 0 && (
            <span className="num px-1.5 py-0.5 rounded bg-action text-white text-[10px]">{activeCount}</span>
          )}
          {triggeredCount > 0 && (
            <span className="num px-1.5 py-0.5 rounded bg-[var(--lt-warning)] text-[#0B0E14] text-[10px]">
              {triggeredCount}
            </span>
          )}
        </button>
      )}

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto">
            <button
              type="button"
              className="fixed inset-0 bg-[#0B0E14]/70"
              aria-label="Close alerts"
              onClick={() => setOpen(false)}
            />
            <div className="relative z-[1] w-full sm:max-w-[480px] max-h-[min(88dvh,880px)] overflow-y-auto rounded-xl border border-hairline bg-surface shadow-[0_24px_64px_rgba(0,0,0,0.45)]">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-hairline bg-surface/95 backdrop-blur-md">
                <div>
                  <div className="lt-label mb-0.5">Alerts</div>
                  <div className="text-sm font-semibold text-ink">Price & day move</div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>

            <div className="px-4 py-3 space-y-3">
              <form onSubmit={handleAdd} className="space-y-2 rounded-md border border-hairline bg-canvas p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="lt-label mb-1 block">Symbol</label>
                    <input
                      value={symbol}
                      onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                      className="w-full h-10 px-2.5 text-xs num lt-input"
                      placeholder="AAPL"
                      required
                    />
                  </div>
                  <div>
                    <label className="lt-label mb-1 block">Type</label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full h-10 px-2.5 text-xs lt-input"
                    >
                      <option value="price_above">Price above</option>
                      <option value="price_below">Price below</option>
                      <option value="pct_up">Day % up</option>
                      <option value="pct_down">Day % down</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="lt-label mb-1 block">
                    {type.startsWith('pct') ? 'Percent' : 'Price'}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    className="w-full h-10 px-2.5 text-xs num lt-input"
                    placeholder={type.startsWith('pct') ? '2.5' : '150.00'}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 h-10 rounded-md bg-action text-white text-xs font-semibold"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Add alert
                  </button>
                  <button
                    type="button"
                    onClick={() => requestPermission()}
                    className="h-10 px-3 rounded-md border border-hairline text-xs text-muted hover:bg-elevated"
                  >
                    Enable notify
                  </button>
                </div>
                {message && <div className="text-[11px] text-bull">{message}</div>}
              </form>

              {triggeredCount > 0 && (
                <button
                  type="button"
                  onClick={clearTriggered}
                  className="text-[11px] text-muted hover:text-ink underline"
                >
                  Reactivate triggered alerts
                </button>
              )}

              {alerts.length === 0 ? (
                <div className="text-xs text-muted py-6 text-center">No alerts yet</div>
              ) : (
                <div className="space-y-2">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="rounded-md border border-hairline bg-canvas px-3 py-2 flex items-center justify-between gap-2"
                    >
                      <button
                        type="button"
                        className="min-w-0 text-left"
                        onClick={() => {
                          selectSymbol(alert.symbol);
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="num text-sm font-semibold text-ink">{alert.symbol}</span>
                          {alert.triggeredAt ? (
                            <span className="text-[9px] uppercase tracking-wider text-[var(--lt-warning)]">
                              Triggered
                            </span>
                          ) : (
                            <span className="text-[9px] uppercase tracking-wider text-bull">Active</span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted mt-0.5">
                          {TYPE_LABELS[alert.type] || alert.type}{' '}
                          <span className="num text-ink">
                            {alert.type.startsWith('pct')
                              ? `${alert.threshold}%`
                              : `$${Number(alert.threshold).toFixed(2)}`}
                          </span>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeAlert(alert.id)}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:text-bear hover:bg-elevated"
                        title="Remove"
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AlertsPanel;
